import type {
  GridContainerLayout,
  GridItemLayout,
  SerializedCompStructure,
  SerializedPageStructure,
  SingleLayoutData,
} from '@wix/document-services-types'

type LayoutData = Partial<SingleLayoutData>
type ScopedLayouts = Record<string, LayoutData>

type WithLayouts = {
  layouts?: LayoutData
  scopedLayouts?: ScopedLayouts
}

const isGridContainerLayout = (
  containerLayout: LayoutData['containerLayout'],
): containerLayout is GridContainerLayout =>
  (containerLayout as GridContainerLayout | undefined)?.type ===
  'GridContainerLayout'

const isGridItemLayout = (
  itemLayout: LayoutData['itemLayout'],
): itemLayout is GridItemLayout =>
  (itemLayout as GridItemLayout | undefined)?.type === 'GridItemLayout'

const getAllLayoutData = (node: WithLayouts): LayoutData[] => [
  ...(node.layouts ? [node.layouts] : []),
  ...Object.values(node.scopedLayouts ?? {}),
]

const getGridRowCounts = (page: WithLayouts): number[] =>
  getAllLayoutData(page)
    .map(({ containerLayout }) =>
      isGridContainerLayout(containerLayout) ? containerLayout.rows.length : 0,
    )
    .filter(rowCount => rowCount > 0)

const getOccupiedRows = (
  components: SerializedCompStructure[],
): Set<number> => {
  const occupiedRows = new Set<number>()
  for (const component of components) {
    for (const layoutData of getAllLayoutData(component)) {
      if (isGridItemLayout(layoutData.itemLayout)) {
        const { rowStart, rowEnd } = layoutData.itemLayout.gridArea
        for (let row = rowStart; row < rowEnd; row++) {
          occupiedRows.add(row)
        }
      }
    }
  }
  return occupiedRows
}

const withReducedGridRows = (
  layoutData: LayoutData,
  occupiedRows: Set<number>,
): LayoutData =>
  isGridContainerLayout(layoutData.containerLayout)
    ? {
        ...layoutData,
        containerLayout: {
          ...layoutData.containerLayout,
          rows: layoutData.containerLayout.rows.filter((_, trackIndex) =>
            occupiedRows.has(trackIndex + 1),
          ),
        },
      }
    : layoutData

const withShiftedGridArea = (
  layoutData: LayoutData,
  shiftRow: (row: number) => number,
): LayoutData =>
  isGridItemLayout(layoutData.itemLayout)
    ? {
        ...layoutData,
        itemLayout: {
          ...layoutData.itemLayout,
          gridArea: {
            ...layoutData.itemLayout.gridArea,
            rowStart: shiftRow(layoutData.itemLayout.gridArea.rowStart),
            rowEnd: shiftRow(layoutData.itemLayout.gridArea.rowEnd),
          },
        },
      }
    : layoutData

const mapLayouts = <T extends WithLayouts>(
  node: T,
  mapLayoutData: (layoutData: LayoutData) => LayoutData,
): T => ({
  ...node,
  ...(node.layouts ? { layouts: mapLayoutData(node.layouts) } : {}),
  ...(node.scopedLayouts
    ? {
        scopedLayouts: Object.fromEntries(
          Object.entries(node.scopedLayouts).map(
            ([breakpointId, layoutData]) => [
              breakpointId,
              mapLayoutData(layoutData),
            ],
          ),
        ),
      }
    : {}),
})

export const removeOrphanedGridRows = <T extends SerializedPageStructure>(
  page: T,
): T => {
  const pageWithLayouts = page as T & WithLayouts

  const gridRowCounts = getGridRowCounts(pageWithLayouts)
  if (gridRowCounts.length === 0) {
    return page
  }

  const occupiedRows = getOccupiedRows(page.components)
  if (occupiedRows.size === 0) {
    return page
  }

  const orphanedRows: number[] = []
  for (let row = 1; row <= Math.max(...gridRowCounts); row++) {
    if (!occupiedRows.has(row)) {
      orphanedRows.push(row)
    }
  }
  if (orphanedRows.length === 0) {
    return page
  }

  const shiftRow = (row: number) =>
    row - orphanedRows.filter(orphanedRow => orphanedRow < row).length

  return {
    ...mapLayouts(pageWithLayouts, layoutData =>
      withReducedGridRows(layoutData, occupiedRows),
    ),
    components: page.components.map(component =>
      mapLayouts(component, layoutData =>
        withShiftedGridArea(layoutData, shiftRow),
      ),
    ),
  }
}
